import {
    MODULE_APPLEPAY,
    MODULE_GOOGLEPAY,
    MODULE_LOAD_FAILED,
    MODULE_LOAD_SUCCESSFULLY,
    MODULE_LOADER,
    moduleApplePay,
    moduleCore,
    moduleGooglePay,
} from '../../constants';
import { createPreloadLinkElement, getElementData, resolveUrl, resolveVersion, setElementData } from '../../Function';
import { buildElementId } from '../../Function/buildElementId';
import { TModule, TModuleResponse, TPreloadComgateCheckoutConfig, TPreloadComgateCheckoutResult, TResolvedVersion } from '../../types';
import { preloadConfigValidator } from './preloadConfigValidator';
import { TPreloadInnerConfig } from './types';

/**
 * Preloads modules for the Comgate Checkout SDK based on the provided configuration.
 *
 * @param {TPreloadComgateCheckoutConfig} config - The configuration object containing settings for preloading. This includes details such as checkoutId, an array of modules to preload, and other optional customization parameters.
 * @return {Promise<TPreloadComgateCheckoutResult>} Returns a promise that resolves to an object containing the preloading status, details of successfully preloaded modules, and any errors encountered during the process.
 */
export async function preloadComgateCheckout(config: TPreloadComgateCheckoutConfig): Promise<TPreloadComgateCheckoutResult> {
    // Validate config
    const validationResponse = preloadConfigValidator(config);
    if (validationResponse !== true) {
        return Promise.reject({
            success: false,
            checkoutId: config?.checkoutId,
            preloaded: [],
            error: {
                [MODULE_LOADER]: new TypeError(validationResponse),
            },
        } as TPreloadComgateCheckoutResult);
    }

    const frozenConfig: TPreloadComgateCheckoutConfig = Object.freeze(JSON.parse(JSON.stringify(config)));

    // Resolve and set CDN base url to window
    const cdnBaseUrl = resolveUrl(frozenConfig, 'cdn');
    const resolvedVersion: TResolvedVersion = await resolveVersion(frozenConfig, cdnBaseUrl);

    return new Promise<TPreloadComgateCheckoutResult>((resolve, reject) => {
        // Initialize the results array and preload Core module
        const preloadResults = [];

        if (frozenConfig.excludeCore !== true) {
            preloadResults.push(preload({ config: frozenConfig, module: moduleCore, cdnBaseUrl, resolvedVersion }));
        }

        if (frozenConfig.modules !== undefined) {
            // Preload Apple Pay module
            if (frozenConfig.modules.includes(MODULE_APPLEPAY)) {
                preloadResults.push(preload({ config: frozenConfig, module: moduleApplePay, cdnBaseUrl, resolvedVersion }));
            }

            // Preload Google Pay module
            if (frozenConfig.modules.includes(MODULE_GOOGLEPAY)) {
                preloadResults.push(preload({ config: frozenConfig, module: moduleGooglePay, cdnBaseUrl, resolvedVersion }));
            }
        }

        Promise.all(preloadResults).then((modulesStatus) => {
            const preloadResponse: TPreloadComgateCheckoutResult = {
                checkoutId: frozenConfig.checkoutId,
                preloaded: [],
                success: true,
            };

            modulesStatus.forEach((module) => {
                if (module.success) {
                    preloadResponse.preloaded.push(module.name);
                } else {
                    preloadResponse.success = false;
                    preloadResponse.error = preloadResponse.error || {};
                    preloadResponse.error[module.name] = module.error!;
                }
            });

            if (frozenConfig.onPreload !== undefined && typeof frozenConfig.onPreload === 'function') {
                frozenConfig.onPreload(preloadResponse);
            }

            // Window.dispatchEvent(new Event(EVENT_MODULES_PRELOADED));

            if (preloadResponse.success) {
                resolve(preloadResponse);
                return;
            }
            reject(preloadResponse);
        });
    });
}

/**
 * Determines whether a given module has been successfully loaded.
 *
 * This function checks for the existence of a `<script>` element with
 * a specific ID in the DOM, which corresponds to the provided module's
 * name. If such an element exists and indicates a successful load,
 * it returns true; otherwise, it returns false.
 *
 * @param {TModule} module - The module to check for successful loading.
 * @returns {boolean} - True if the module is successfully loaded, otherwise false.
 */
const isModuleLoaded = (module: TModule) => {
    const moduleLoadId = buildElementId('load', module.name);
    const element = document.querySelector<HTMLElementTagNameMap['script']>(`#${moduleLoadId}`);
    if (element) {
        const success = getElementData(element, 'success');
        if (success === MODULE_LOAD_SUCCESSFULLY) {
            return true;
        }
    }
    return false;
};

/**
 * Asynchronously preloads a module by injecting a link element into the document head
 * and resolves when the module is successfully preloaded or already loaded.
 *
 * @param {TPreloadInnerConfig} preloadConfig - Configuration object for the module preload process.
 * @param {string} preloadConfig.module.name - The name of the module to be preloaded.
 * @param {string} preloadConfig.module.file - The filename or path of the module file to be preloaded.
 * @param {string} preloadConfig.resolvedVersion - The resolved version of the module to load.
 * @param {string} preloadConfig.cdnBaseUrl - The base URL for the module's CDN.
 *
 * @returns {Promise<TModuleResponse>} A promise that resolves with the preload result object, containing success
 * status and module name, or rejects with an error in case of failure.
 *
 * @throws {Error} Throws an error when an unexpected error occurs during the preload process.
 */
const preload = async (preloadConfig: TPreloadInnerConfig): Promise<TModuleResponse> =>
    new Promise((resolve, reject) => {
        let element: HTMLElementTagNameMap['link'] | null = null;

        try {
            if (isModuleLoaded(preloadConfig.module)) {
                resolve({
                    success: true,
                    name: preloadConfig.module.name,
                });
                return;
            }

            const preloadId = buildElementId('preload', preloadConfig.module.name);
            element = document.querySelector<HTMLElementTagNameMap['link']>(`#${preloadId}`);

            // Try to re-preload the script if it was not loaded correctly
            if (element) {
                resolve({
                    success: true,
                    name: preloadConfig.module.name,
                });
            } else {
                const preload = createPreloadLinkElement(preloadId, preloadConfig.resolvedVersion, preloadConfig.cdnBaseUrl, preloadConfig.module.file);

                document.head.append(preload);
                resolve({
                    success: true,
                    name: preloadConfig.module.name,
                });
            }
        } catch (error) {
            setElementData(element, 'success', MODULE_LOAD_FAILED);
            reject({
                success: false,
                name: preloadConfig.module.name,
                error: new Error('Unexpected error.', { cause: error }),
            });
        }
    });
